/* ==========================================================================
   NIG 使用例ギャラリー — 動画の再生制御と拡大表示
   対象テーマ: Shopify Dawn 15.5.0

   ■ グリッド内の動画
   全部を同時に再生するとモバイルが重くなり、通信量も無駄になる。そこで
     ・画面内に入ったものだけ再生し、出たら止める（IntersectionObserver）
     ・同時再生は最大 4 本。超える場合は画面中央に近いものを優先する
     ・自動再生は必ず無音（muted）。音声はモーダルでのユーザー操作でのみ
     ・データセーバー / 低速回線 / アニメーション低減の設定時は自動再生しない
       （ポスター画像のままにする）
   YouTube はグリッドに iframe を作らない。ポスター画像だけを出し、
   モーダルを開いたときに iframe を生成する。

   ■ 拡大表示（モーダル）
   画像・動画のどちらも同じモーダルで扱う。中身は textContent と
   要素生成で組み立て、innerHTML は使わない（入力値をそのまま扱うため）。
   閉じたら中身ごと破棄するので、動画は確実に停止する。
   ========================================================================== */
(function () {
  'use strict';

  var MAX_CONCURRENT = 4;

  var root = document.querySelector('[data-voices]');
  var modal = document.querySelector('[data-voices-modal]');
  if (!root) return;

  /* 自動再生を避けるべき環境か */
  function shouldHoldBack() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      if (connection.saveData) return true;
      if (/^(slow-2g|2g)$/.test(connection.effectiveType || '')) return true;
    }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    return false;
  }

  /* ------------------------------------------------------------------ *
   * グリッド内の動画
   * ------------------------------------------------------------------ */
  var videos = Array.prototype.slice.call(root.querySelectorAll('.voices-card__video'))
    .map(function (el) { return { el: el, visible: false }; });

  if (videos.length > 0 && !shouldHoldBack() && 'IntersectionObserver' in window) {
    var byElement = new Map();
    videos.forEach(function (item) { byElement.set(item.el, item); });

    var scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        reevaluate();
      });
    }

    function reevaluate() {
      var middle = window.innerHeight / 2;
      var candidates = videos
        .filter(function (item) {
          return item.visible && !item.el.closest('[data-voices-card]').hidden;
        })
        .map(function (item) {
          var rect = item.el.getBoundingClientRect();
          return { item: item, distance: Math.abs(rect.top + rect.height / 2 - middle) };
        })
        .sort(function (a, b) { return a.distance - b.distance; })
        .slice(0, MAX_CONCURRENT);

      var playing = new Set(candidates.map(function (c) { return c.item; }));
      videos.forEach(function (item) {
        if (playing.has(item)) {
          if (item.el.paused) {
            item.el.muted = true; // 念のため。無音以外で自動再生はしない
            var promise = item.el.play();
            if (promise && promise.catch) promise.catch(function () { /* 再生拒否は無視 */ });
          }
        } else if (!item.el.paused) {
          item.el.pause();
        }
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var item = byElement.get(entry.target);
        if (item) item.visible = entry.isIntersecting;
      });
      schedule();
    }, { rootMargin: '100px 0px', threshold: 0.25 });

    videos.forEach(function (item) { observer.observe(item.el); });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('voices:filtered', schedule);

    // タブを離れたら止める（バックグラウンド再生を残さない）
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        videos.forEach(function (item) { if (!item.el.paused) item.el.pause(); });
      } else {
        schedule();
      }
    });
  }

  function pauseAllGridVideos() {
    videos.forEach(function (item) { if (!item.el.paused) item.el.pause(); });
  }

  /* ------------------------------------------------------------------ *
   * 拡大表示
   * ------------------------------------------------------------------ */
  if (!modal) return;

  var mediaBox = modal.querySelector('[data-voices-modal-media]');
  var quoteNode = modal.querySelector('[data-voices-modal-quote]');
  var authorNode = modal.querySelector('[data-voices-modal-author]');
  var productNode = modal.querySelector('[data-voices-modal-product]');
  var ctaNode = modal.querySelector('[data-voices-modal-cta]');
  var closeButton = modal.querySelector('.voices-modal__close');
  var lastFocused = null;

  function clearMedia() {
    if (!mediaBox) return;
    var video = mediaBox.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    while (mediaBox.firstChild) mediaBox.removeChild(mediaBox.firstChild);
  }

  function sendPlayEvent(card) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'play_voice_video', {
      voice_product: card.getAttribute('data-product') || '',
      voice_product_label: card.getAttribute('data-product-label') || ''
    });
  }

  function buildMedia(card) {
    var mediaType = card.getAttribute('data-media');
    var videoUrl = card.getAttribute('data-video') || '';
    var youtubeId = card.getAttribute('data-youtube') || '';
    var imageUrl = card.getAttribute('data-image') || '';
    var label = card.getAttribute('data-product-label') || '';

    if (mediaType === 'video' && youtubeId) {
      var frame = document.createElement('div');
      frame.className = 'voices-modal__frame';
      var iframe = document.createElement('iframe');
      iframe.src = 'https://www.youtube.com/embed/' + encodeURIComponent(youtubeId) +
        '?rel=0&playsinline=1&autoplay=1';
      iframe.title = label + 'の使用例動画';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      iframe.setAttribute('allowfullscreen', '');
      frame.appendChild(iframe);
      mediaBox.appendChild(frame);
      sendPlayEvent(card);
      return;
    }

    if (mediaType === 'video' && videoUrl) {
      var video = document.createElement('video');
      video.src = videoUrl;
      if (imageUrl) video.poster = imageUrl;
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      mediaBox.appendChild(video);
      var promise = video.play();
      if (promise && promise.catch) {
        // 音声付きの自動再生が拒否されたら、操作ボタンだけ出して待つ
        promise.catch(function () { video.controls = true; });
      }
      sendPlayEvent(card);
      return;
    }

    var img = document.createElement('img');
    img.src = imageUrl;
    img.alt = label + 'の使用例';
    mediaBox.appendChild(img);
  }

  function open(card) {
    lastFocused = document.activeElement;
    pauseAllGridVideos();
    clearMedia();
    buildMedia(card);

    var quote = card.getAttribute('data-quote') || '';
    var author = card.getAttribute('data-author') || '';
    var label = card.getAttribute('data-product-label') || '';
    var url = card.getAttribute('data-product-url') || '';

    if (quoteNode) quoteNode.textContent = quote;
    if (authorNode) authorNode.textContent = author;
    if (productNode) productNode.textContent = label;
    if (ctaNode) {
      if (url) {
        ctaNode.href = url;
        ctaNode.hidden = false;
      } else {
        ctaNode.removeAttribute('href');
        ctaNode.hidden = true;
      }
    }

    modal.hidden = false;
    document.body.classList.add('voices-modal-open');
    if (closeButton) closeButton.focus();
  }

  function close() {
    if (modal.hidden) return;
    clearMedia();
    modal.hidden = true;
    document.body.classList.remove('voices-modal-open');
    if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    lastFocused = null;
    document.dispatchEvent(new CustomEvent('voices:filtered')); // 再生対象を組み直す
  }

  root.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-voices-open]') : null;
    if (!trigger) return;
    var card = trigger.closest('[data-voices-card]');
    if (card) open(card);
  });

  modal.addEventListener('click', function (event) {
    if (event.target.closest('[data-voices-close]')) close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  // モーダル内でのタブ移動を閉じるボタンと CTA の間に留める
  modal.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    var focusable = modal.querySelectorAll('button, a[href], video, iframe');
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
